import {
    Control,
    Controller,
    FieldError,
    FieldValues,
    Path,
} from 'react-hook-form';
import {
    AutocompleteProps,
    AutocompleteRenderOptionState,
    FormControl,
    InputAdornment,
} from '@mui/material';
import { IOption, ISize } from '../interface';
import {
    Checkbox,
    PargodaInfoIcon,
    TextField,
    useTheme,
} from '../../base-components';
import { HTMLAttributes } from 'react';
import { TextFieldProps } from '@mui/material/TextField';
import { StyledAutoComplete } from './FormSelector.styles';
import { TooltipPlacement } from '../../../common/consts';

type OmitAutocompletePropsRenderInput<T> = Omit<T, 'renderInput'>;
interface Props<F extends FieldValues>
    extends OmitAutocompletePropsRenderInput<
        AutocompleteProps<
            IOption,
            boolean | undefined,
            boolean | undefined,
            boolean | undefined
        >
    > {
    name: Path<F>;
    control: Control<F>;
    options: Array<IOption>;
    label?: string;
    required?: boolean;
    multiple?: boolean;
    multiWithoutCheckbox?: boolean;
    size?: ISize;
    disabled?: boolean;
    placeholder?: string;
    tooltipEndAdornment?: {
        text: string;
        color?: string;
        location?: TooltipPlacement;
    };
    displayErrorInEndAdornment?: boolean;
    disableClearable?: boolean;
}
const FormSelector = <F extends FieldValues>(props: Props<F>) => {
    const {
        name,
        control,
        label,
        required = false,
        multiple,
        size = 'small',
        disabled = false,
        placeholder,
        renderOption,
        options = [],
        multiWithoutCheckbox = false,
        tooltipEndAdornment,
        displayErrorInEndAdornment,
        disableClearable = false,
    } = props;
    const theme = useTheme();

    const renderTooltip = (
        text: string | undefined,
        location?: TooltipPlacement | undefined,
        color?: string | undefined,
        iconName?: string | undefined
    ) => (
        <InputAdornment position="start">
            <PargodaInfoIcon
                text={text ?? ''}
                placement={location}
                color={color}
                iconName={iconName}
            />
        </InputAdornment>
    );

    const getFinalEndAdornment = (error: FieldError | undefined) => {
        if (error && displayErrorInEndAdornment) {
            return renderTooltip(
                error.message,
                TooltipPlacement.TOP_START,
                theme.palette.error.main,
                'ErrorOutlineOutlinedIcon'
            );
        } else if (tooltipEndAdornment) {
            return renderTooltip(
                tooltipEndAdornment.text,
                tooltipEndAdornment.location,
                tooltipEndAdornment.color
            );
        }
        return null;
    };

    const renderInput = (error: FieldError | undefined) => {
        return (params: TextFieldProps) => {
            return (
                <TextField
                    {...params}
                    error={!!error}
                    required={required}
                    disabled={disabled}
                    helperText={error && error.message}
                    label={label}
                    size={size}
                    placeholder={placeholder}
                    inputProps={{
                        ...params.inputProps,
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {getFinalEndAdornment(error)}
                                {params.InputProps?.endAdornment}
                            </>
                        ),
                        sx: { input: { minHeight: '23px' } },
                    }}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderRadius: '15px',
                        },
                    }}
                />
            );
        };
    };

    const defaultMultiRenderOption = (
        props: HTMLAttributes<HTMLLIElement>,
        option: IOption,
        { selected }: AutocompleteRenderOptionState
    ) => (
        <li {...props} key={option.value}>
            {!multiWithoutCheckbox && (
                <Checkbox
                    // icon={<Icons.CheckBoxOutlineBlankIcon fontSize="small" />}
                    // checkedIcon={<Icons.CheckBoxIcon fontSize="small" />}
                    sx={{ marginInlineEnd: 1 }}
                    checked={selected}
                />
            )}
            {option.label || ''}
        </li>
    );

    const defaultRenderOption = (
        props: HTMLAttributes<HTMLLIElement>,
        option: IOption
    ) => {
        // TODO: fix option specific disable.;
        // if (option.disabled) {
        //     return (
        //         <Tooltip title={option.tooltipText} key={option.key}>
        //             <div>
        //                 <ListItemButton disabled {...props}>
        //                     {option.label || ''}
        //                 </ListItemButton>
        //             </div>
        //         </Tooltip>
        //     );
        // }
        return (
            <li {...props} key={option.value}>
                {option.label || ''}
            </li>
        );
    };

    let renderOptionFunc = renderOption;
    if (!renderOptionFunc) {
        renderOptionFunc = multiple
            ? defaultMultiRenderOption
            : defaultRenderOption;
    }

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth>
                    <StyledAutoComplete
                        {...field}
                        multiple={multiple}
                        disableCloseOnSelect={multiple}
                        filterSelectedOptions={multiple}
                        id={label}
                        blurOnSelect={!multiple}
                        isOptionEqualToValue={(opt: IOption, val: IOption) => {
                            return opt?.value === val?.value;
                        }}
                        options={options}
                        renderOption={renderOptionFunc}
                        onChange={(_e, val: IOption) => {
                            field.onChange(val);
                        }}
                        disabled={disabled}
                        renderInput={renderInput(error)}
                        disableClearable={disableClearable}
                    />
                </FormControl>
            )}
        />
    );
};

export default FormSelector;
